/** Raport zarządu — sekcja close: etykiety pól formularzy, pozycji i komunikaty walidacji (część `mbr.close.*`). */
type Shape<T> = { [K in keyof T]: T[K] extends string ? string : Shape<T[K]> };

export const mbrCloseFieldsPl = {
  issues: {
    NOT_AN_OBJECT: 'Nieprawidłowe dane formularza.',
    TOO_MANY_ROWS: 'Za dużo wierszy.',
    BAD_ROW_KEY: 'Nieprawidłowa pozycja wiersza.',
    BAD_BRANCH: 'Nieznany oddział.',
    DUPLICATE_ROW: 'Ta pozycja występuje dwa razy.',
    UNKNOWN_FIELD: 'Nieznane pole.',
    REQUIRED: 'Pole wymagane.',
    NOT_A_NUMBER: 'To nie jest liczba.',
    NOT_INTEGER: 'Wymagana liczba całkowita.',
    NEGATIVE: 'Wartość nie może być ujemna.',
    BAD_DATE: 'Nieprawidłowa data.',
    DATE_OUTSIDE_PERIOD: 'Data spoza okresu raportu.',
    PAYROLL_COST_ORDER: 'Oczekiwane: koszt pracodawcy ≥ brutto ≥ netto.',
    RECEIVABLE_BUCKETS_SUM: 'Przedziały < 30 i > 30 dni nie sumują się do przeterminowanych.',
    RECEIVABLE_WEEK_AGO_COPY: '„7 dni wstecz” identyczne z bieżącym — czy to nie skopiowana kolumna?',
    NOTE_TOO_LONG: 'Uwagi są za długie.',
  },
  rowKey: {
    branch: 'Oddział',
    fleet: 'Nr rejestracyjny',
    drivers: 'Kierowca',
    costs: 'Kategoria kosztu',
    hr: 'Dział',
    receivables: 'Rodzaj',
    collections: 'Etap',
    rowBranch: 'Oddział',
  },
  branches: { _: 'Cała firma', GD: 'Gdańsk', ZK: 'Koszalin', CT: 'Toruń' },
  fields: {
    ordersCount: 'Zamówienia',
    pickingHours: 'Godziny kompletacji',
    orderLines: 'Pozycje w zamówieniach',
    inventoryPlus: 'Inwentaryzacja „+” (zł)',
    inventoryMinus: 'Inwentaryzacja „−” (zł)',
    disposalValue: 'Utylizacja po korektach (zł)',
    QUALITY: 'Reklamacje: jakość',
    MISSING_IN_DELIVERY: 'Reklamacje: brak w dostawie',
    MISSING_ON_DOCUMENT: 'Reklamacje: brak na dokumencie',
    WEIGHT_QUANTITY: 'Reklamacje: waga / ilość',
    WRONG_PRODUCT: 'Reklamacje: błąd towarowy',
    EXPIRY_DATE: 'Reklamacje: termin przydatności',
    PRICE: 'Reklamacje: cena',
    OTHER: 'Reklamacje: inne',
    salesValue: 'Sprzedaż wyrobów gotowych (zł)',
    costValue: 'Koszt wytworzenia (zł)',
    quantity: 'Ilość',
    km: 'Km',
    kmLimit: 'Limit km',
    fuelCost: 'Paliwo (zł)',
    rentCost: 'Najem (zł)',
    excessMileageCost: 'Nadprzebieg (zł)',
    otherCost: 'Inne (zł)',
    hours: 'Godziny',
    overtimeHours: 'Nadgodziny',
    sickLeaveDays: 'L4 (dni)',
    vacationDays: 'Urlop (dni)',
    amount: 'Kwota (zł)',
    headcount: 'Liczba osób',
    grossPay: 'Brutto (zł)',
    bonuses: 'Premie (zł)',
    netPay: 'Netto (zł)',
    employerCost: 'Koszt pracodawcy (zł)',
    date: 'Na dzień',
    balance: 'Stan należności',
    notDue: 'Niewymagalne',
    overdue: 'Przeterminowane',
    overdueUpTo30: 'Przeterminowane < 30 dni',
    overdueOver30: 'Przeterminowane > 30 dni',
    repayments7d: 'Spłaty z 7 dni',
    weekAgoBalance: 'Stan 7 dni wstecz',
    weekAgoOverdue: 'Przeterminowane 7 dni wstecz',
    casesCount: 'Liczba spraw',
  },
  costCategories: {
    fixedOther: 'Koszty stałe pozostałe',
    variableOther: 'Koszty zmienne pozostałe',
    payroll: 'Wynagrodzenia (koszt pracodawcy)',
    vehiclesRent: 'Pojazdy — najem, leasing',
    vehiclesOperation: 'Pojazdy — eksploatacja',
    premises: 'Wynajem powierzchni',
    energy: 'Energia, woda i ścieki',
    packaging: 'Opakowania',
    transportPurchase: 'Usługi transportowe (zakup towaru)',
    transportB2b: 'Usługi transportowe B2B',
    equipmentLeasing: 'Leasing urządzeń',
  },
  receivableKinds: { FACTORING: 'Faktoring', OTHER: 'Pozostałe' },
  stages: { enforcement: 'postępowanie egzekucyjne', court: 'postępowanie sądowe', other: 'inne' },
};

export const mbrCloseFieldsEn: Shape<typeof mbrCloseFieldsPl> = {
  issues: {
    NOT_AN_OBJECT: 'Invalid form data.',
    TOO_MANY_ROWS: 'Too many rows.',
    BAD_ROW_KEY: 'Invalid row item.',
    BAD_BRANCH: 'Unknown branch.',
    DUPLICATE_ROW: 'This item appears twice.',
    UNKNOWN_FIELD: 'Unknown field.',
    REQUIRED: 'Required.',
    NOT_A_NUMBER: 'Not a number.',
    NOT_INTEGER: 'A whole number is required.',
    NEGATIVE: 'Cannot be negative.',
    BAD_DATE: 'Invalid date.',
    DATE_OUTSIDE_PERIOD: 'Date outside the report period.',
    PAYROLL_COST_ORDER: 'Expected: employer cost ≥ gross ≥ net.',
    RECEIVABLE_BUCKETS_SUM: 'The < 30 and > 30 days buckets do not add up to overdue.',
    RECEIVABLE_WEEK_AGO_COPY: '“7 days back” identical to today — a copied column?',
    NOTE_TOO_LONG: 'Notes are too long.',
  },
  rowKey: {
    branch: 'Branch',
    fleet: 'Plate number',
    drivers: 'Driver',
    costs: 'Cost category',
    hr: 'Department',
    receivables: 'Kind',
    collections: 'Stage',
    rowBranch: 'Branch',
  },
  branches: { _: 'Whole company', GD: 'Gdańsk', ZK: 'Koszalin', CT: 'Toruń' },
  fields: {
    ordersCount: 'Orders',
    pickingHours: 'Picking hours',
    orderLines: 'Order lines',
    inventoryPlus: 'Stock-take “+” (PLN)',
    inventoryMinus: 'Stock-take “−” (PLN)',
    disposalValue: 'Disposal after corrections (PLN)',
    QUALITY: 'Complaints: quality',
    MISSING_IN_DELIVERY: 'Complaints: missing in delivery',
    MISSING_ON_DOCUMENT: 'Complaints: missing on document',
    WEIGHT_QUANTITY: 'Complaints: weight / quantity',
    WRONG_PRODUCT: 'Complaints: wrong product',
    EXPIRY_DATE: 'Complaints: expiry date',
    PRICE: 'Complaints: price',
    OTHER: 'Complaints: other',
    salesValue: 'Finished goods sales (PLN)',
    costValue: 'Production cost (PLN)',
    quantity: 'Quantity',
    km: 'Km',
    kmLimit: 'Km limit',
    fuelCost: 'Fuel (PLN)',
    rentCost: 'Rent (PLN)',
    excessMileageCost: 'Excess mileage (PLN)',
    otherCost: 'Other (PLN)',
    hours: 'Hours',
    overtimeHours: 'Overtime',
    sickLeaveDays: 'Sick leave (days)',
    vacationDays: 'Holiday (days)',
    amount: 'Amount (PLN)',
    headcount: 'Headcount',
    grossPay: 'Gross (PLN)',
    bonuses: 'Bonuses (PLN)',
    netPay: 'Net (PLN)',
    employerCost: 'Employer cost (PLN)',
    date: 'As of',
    balance: 'Balance',
    notDue: 'Not due',
    overdue: 'Overdue',
    overdueUpTo30: 'Overdue < 30 days',
    overdueOver30: 'Overdue > 30 days',
    repayments7d: 'Repayments, 7 days',
    weekAgoBalance: 'Balance 7 days back',
    weekAgoOverdue: 'Overdue 7 days back',
    casesCount: 'Cases',
  },
  costCategories: {
    fixedOther: 'Other fixed costs',
    variableOther: 'Other variable costs',
    payroll: 'Payroll (employer cost)',
    vehiclesRent: 'Vehicles — rent, lease',
    vehiclesOperation: 'Vehicles — running costs',
    premises: 'Premises rent',
    energy: 'Energy, water and sewage',
    packaging: 'Packaging',
    transportPurchase: 'Transport services (purchasing)',
    transportB2b: 'Transport services B2B',
    equipmentLeasing: 'Equipment leasing',
  },
  receivableKinds: { FACTORING: 'Factoring', OTHER: 'Other' },
  stages: { enforcement: 'enforcement proceedings', court: 'court proceedings', other: 'other' },
};
